import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import SuspendModal from '../components/platform/SuspendModal';
import OrgEditModal from '../components/platform/OrgEditModal';
import OrgDetailModal from '../components/platform/OrgDetailModal';
import AttentionCard from '../components/platform/AttentionCard';
import PaymentsCard from '../components/platform/PaymentsCard';
import InvoicesCard from '../components/platform/InvoicesCard';
import PromoCodesCard from '../components/platform/PromoCodesCard';
import FunnelCard from '../components/platform/FunnelCard';
import LeadsCard from '../components/platform/LeadsCard';
import CohortsCard from '../components/platform/CohortsCard';
import OrgsCard from '../components/platform/OrgsCard';
import PlansCard from '../components/platform/PlansCard';
import TicketsCard from '../components/platform/TicketsCard';
import ModulesCard from '../components/platform/ModulesCard';
import NotificationBell from '../components/common/NotificationBell';
import {
  getPlatformMetrics, getPlatformOrganizations, updatePlatformOrganization,
  getPlatformPlans, getPlatformAttention, getPlatformSupportTickets,
  impersonateOrganization,
} from '../services/api';
import '../components/common/ui.css';
import './PlatformPage.css';

const TABS = [
  { key: 'overview', label: 'Обзор' },
  { key: 'orgs', label: 'Организации' },
  { key: 'finance', label: 'Финансы' },
  { key: 'growth', label: 'Рост' },
  { key: 'plans', label: 'Тарифы' },
  { key: 'modules', label: 'Модули' },
  { key: 'support', label: 'Поддержка' },
];

const PlatformPage = () => {
  const { user, isPlatformAdmin, isAuthenticated, loading: authLoading, logout } = useAuth();

  const [tab, setTab] = useState('overview');

  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);

  const [metrics, setMetrics] = useState(null);

  const [attention, setAttention] = useState(null);
  const [attentionLoading, setAttentionLoading] = useState(true);

  const [openTickets, setOpenTickets] = useState(0);

  const [suspendTarget, setSuspendTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [detailOrgId, setDetailOrgId] = useState(null);
  const [actionError, setActionError] = useState('');

  const planById = plans.reduce((acc, plan) => { acc[plan.id] = plan; return acc; }, {});

  const loadMetrics = useCallback(async () => {
    try {
      setMetrics(await getPlatformMetrics());
    } catch (e) {
      // не критично для остальной страницы — просто не покажем плитки
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const data = await getPlatformOrganizations();
      setOrgs(data.organizations || []);
      setError('');
    } catch (e) {
      setError('Не удалось загрузить список организаций');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPlans = useCallback(async () => {
    try {
      const data = await getPlatformPlans();
      setPlans(data.plans || []);
    } finally {
      setPlansLoading(false);
    }
  }, []);

  const loadAttention = useCallback(async () => {
    try {
      setAttention(await getPlatformAttention());
    } catch (e) {
      setAttention({ items: [], counts: { high: 0, medium: 0, low: 0 } });
    } finally {
      setAttentionLoading(false);
    }
  }, []);

  // Счётчик на вкладке — тикеты, ждущие ответа платформы, нужен сразу,
  // а не при первом открытии раздела поддержки. Считаем так же, как TicketsCard.
  const loadTicketCount = useCallback(async () => {
    try {
      const data = await getPlatformSupportTickets();
      setOpenTickets((data.tickets || []).filter((t) => t.status !== 'closed' && !t.last_message_from_platform).length);
    } catch (e) {
      // счётчик — украшение, молчим
    }
  }, []);

  useEffect(() => {
    load();
    loadPlans();
    loadMetrics();
    loadAttention();
    loadTicketCount();
  }, [load, loadPlans, loadMetrics, loadAttention, loadTicketCount]);

  if (!isAuthenticated) return null;
  if (!authLoading && user && !isPlatformAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  // После действия над организацией пересчитываем и список «требует внимания»:
  // разблокировка или смена тарифа обычно как раз убирает оттуда строку.
  const refreshOrgs = async () => {
    await Promise.all([load(), loadAttention(), loadMetrics()]);
  };

  const handleActivate = async (orgId) => {
    await updatePlatformOrganization(orgId, { status: 'active' });
    await refreshOrgs();
  };

  const handleSuspend = async (orgId, reason) => {
    await updatePlatformOrganization(orgId, { status: 'suspended', suspended_reason: reason });
    await refreshOrgs();
  };

  const handleEditSave = async (orgId, payload) => {
    await updatePlatformOrganization(orgId, payload);
    await refreshOrgs();
  };

  const handleImpersonate = async (orgId) => {
    setActionError('');
    // Вкладку открываем синхронно, до await: браузер блокирует window.open,
    // вызванный уже после сетевого запроса, как всплывающее окно.
    const viewTab = window.open('', '_blank');
    try {
      const data = await impersonateOrganization(orgId);
      if (viewTab) viewTab.location.href = data.url;
      else window.location.href = data.url;
    } catch (err) {
      if (viewTab) viewTab.close();
      setActionError(err.response?.data?.error || 'Не удалось открыть режим просмотра');
    }
  };

  const attentionCount = attention ? attention.counts.high + attention.counts.medium : 0;

  return (
    <div className="platform-page">
      <header className="platform-header">
        <div className="platform-header-brand">
          <span className="platform-header-logo" aria-hidden="true">SaaS</span>
          <span>Управление организациями</span>
        </div>
        <div className="platform-header-actions">
          <NotificationBell />
          <button type="button" className="ui-btn-ghost" onClick={logout}>Выйти</button>
        </div>
      </header>

      <div className="platform-body">
        <div className="ui-tabs">
          {TABS.map(({ key, label }) => (
            <button
              type="button"
              key={key}
              className={`ui-tab ${tab === key ? 'active' : ''}`}
              onClick={() => setTab(key)}
            >
              {label}
              {key === 'overview' && attentionCount > 0 && (
                <span className="platform-tab-count">{attentionCount}</span>
              )}
              {key === 'support' && openTickets > 0 && (
                <span className="platform-tab-count">{openTickets}</span>
              )}
            </button>
          ))}
        </div>

        {actionError && <div className="ui-inline-error">{actionError}</div>}

        {tab === 'overview' && (
          <>
            <AttentionCard
              data={attention}
              loading={attentionLoading}
              onOpenOrg={setDetailOrgId}
            />

            {metrics && (
              <section className="ui-card">
                <div className="ui-card-head">
                  <h2>Метрики</h2>
                  <p>Снимок на текущий момент, без истории по датам.</p>
                </div>
                <div className="ui-metrics-grid">
                  <div className="ui-metric-tile">
                    <div className="ui-metric-value">{metrics.mrr.toLocaleString('ru-RU')} ₽</div>
                    <div className="ui-metric-label">MRR</div>
                  </div>
                  <div className="ui-metric-tile">
                    <div className="ui-metric-value">{metrics.by_status.active}</div>
                    <div className="ui-metric-label">Активных</div>
                  </div>
                  <div className="ui-metric-tile">
                    <div className="ui-metric-value">{metrics.by_status.trial}</div>
                    <div className="ui-metric-label">В триале</div>
                  </div>
                  <div className="ui-metric-tile">
                    <div className="ui-metric-value">{metrics.by_status.suspended}</div>
                    <div className="ui-metric-label">Приостановлено</div>
                  </div>
                  <div className="ui-metric-tile">
                    <div className="ui-metric-value">
                      {metrics.trial_conversion_estimate === null
                        ? '—'
                        : `${Math.round(metrics.trial_conversion_estimate * 100)}%`}
                    </div>
                    <div className="ui-metric-label">Конверсия триала (оценка)</div>
                  </div>
                </div>
              </section>
            )}
          </>
        )}

        {tab === 'orgs' && (
          <OrgsCard
            orgs={orgs}
            loading={loading}
            error={error}
            planById={planById}
            onChanged={refreshOrgs}
            onDetail={setDetailOrgId}
            onEdit={setEditTarget}
            onSuspend={setSuspendTarget}
            onActivate={handleActivate}
            onImpersonate={handleImpersonate}
          />
        )}

        {tab === 'finance' && (
          <>
            <PaymentsCard finance={metrics?.finance} orgs={orgs} />
            <InvoicesCard orgs={orgs} planById={planById} />
          </>
        )}

        {tab === 'growth' && (
          <>
            <FunnelCard />
            <LeadsCard />
            <CohortsCard />
            <PromoCodesCard />
          </>
        )}

        {tab === 'plans' && (
          <PlansCard
            plans={plans}
            loading={plansLoading}
            orgs={orgs}
            onChanged={loadPlans}
          />
        )}

        {tab === 'modules' && <ModulesCard />}

        {tab === 'support' && (
          <TicketsCard onCountChange={setOpenTickets} />
        )}
      </div>

      {suspendTarget && (
        <SuspendModal
          orgName={suspendTarget.name}
          onSubmit={(reason) => handleSuspend(suspendTarget.id, reason)}
          onClose={() => setSuspendTarget(null)}
        />
      )}

      {editTarget && (
        <OrgEditModal
          org={editTarget}
          plans={plans}
          onSubmit={(payload) => handleEditSave(editTarget.id, payload)}
          onClose={() => setEditTarget(null)}
        />
      )}

      {detailOrgId && (
        <OrgDetailModal
          orgId={detailOrgId}
          planById={planById}
          onImpersonate={handleImpersonate}
          onClose={() => setDetailOrgId(null)}
        />
      )}
    </div>
  );
};

export default PlatformPage;
